/**
 * @component ry-example
 * @category  display
 * @summary   Docs/demo helper (ry-ui-only). Single-source side-by-side viewer: a `<template>` child becomes both the rendered live preview AND a `<ry-code language="html">` block of the source. Optionally include a `<script slot="js">` for a collapsible "JS Usage" section. Used by ry-ui's own kitchen-sink demos.
 *
 * @attribute title    string?              Caption forwarded to the inner `<ry-code>` title. Default `"Example"`.
 * @attribute stacked  boolean   default=false   Layout hint — applied as `[data-stacked]` on the host so CSS can switch preview-above-code instead of preview-beside-code.
 *
 * @content   Required:
 *   - `<template>` child — innerHTML is BOTH the live preview AND the displayed code.
 *   Optional:
 *   - `<script slot="js" type="text/plain">` — JS usage example, shown in a collapsible panel.
 *
 * @dom
 *   ry-example[title?][stacked?]
 *     > div.ry-example__preview   [data-ry-target="preview"]   {transformed template content, live}
 *     > div.ry-example__code      [data-ry-target="code"]
 *       > ry-code[language="html"][title]   (auto-injected)
 *     > div.ry-example__usage     [data-ry-target="usage"]   (only when <script slot="js"> present)
 *       > button.ry-example__usage-toggle   aria-expanded
 *       > div.ry-example__usage-panel       hidden by default
 *         > ry-code[language="js"]
 *
 * @gotcha  The `<template>` child is REQUIRED. Without it, setup() returns early — the component renders nothing visible. Authoring HTML directly as children won't work (browser parses it as DOM); always wrap in `<template>`.
 *
 * @gotcha  Live preview runs the source through `transform()` — the ry-ui transform that rewrites unprefixed `<accordion>` → `<ry-accordion>` etc. Authored markup using bare tag names (no `ry-` prefix) will be auto-prefixed for the live preview, but the SHOWN code keeps the original bare form. Consumers see the clean unprefixed source.
 *
 * @gotcha  Indentation is auto-trimmed: leading/trailing blank lines stripped, then common-prefix whitespace removed. Authoring with deep indentation is safe — the rendered code is normalized to flush-left.
 *
 * @gotcha  The JS usage section is COLLAPSED by default (`hidden`). Consumers must click "JS Usage" to expand. There's no way to default-open via attribute — open it manually if needed.
 *
 * @gotcha  Setup-once: editing the `<template>` after mount does NOT update the preview or code. Re-mount the element to refresh.
 *
 * @example basic
 * ```html
 * <ry-example title="Button">
 *   <template>
 *     <button>Click me</button>
 *   </template>
 * </ry-example>
 * ```
 *
 * @example with-prefix-transform
 * ```html
 * <ry-example title="Accordion">
 *   <template>
 *     <accordion>
 *       <accordion-item title="FAQ" open>Hidden content</accordion-item>
 *     </accordion>
 *   </template>
 * </ry-example>
 * ```
 *
 * @example with-js-usage-panel
 * ```html
 * <ry-example title="Switch">
 *   <template>
 *     <switch>Notifications</switch>
 *   </template>
 *   <script slot="js" type="text/plain">
 *     element.addEventListener('ry:change', (e) => {
 *       console.log(e.detail.value);
 *     });
 *   </script>
 * </ry-example>
 * ```
 *
 * @example stacked-layout
 * ```html
 * <ry-example title="Tabs" stacked>
 *   <template>
 *     <tabs><tab title="A">A</tab><tab title="B">B</tab></tabs>
 *   </template>
 * </ry-example>
 * ```
 *
 * @tutorial requires-template-child
 * ```js
 * // Wrong assumption: any inner content works. The wrapper RETURNS EARLY
 * // if no <template> is found — nothing renders.
 * const el = mount('<ry-example title="Bad"><div>plain</div></ry-example>');
 * await expectEqual(el.querySelector('[data-ry-target="preview"]'), null);
 * await expectEqual(el.querySelector('[data-ry-target="code"]'), null);
 * ```
 *
 * @tutorial mount-builds-preview-and-code-slots
 * ```js
 * const wrap = document.createElement('div');
 * wrap.innerHTML = '<ry-example title="X"><template><span>hi</span></template></ry-example>';
 * document.body.appendChild(wrap);
 * await sleep(20);
 * const el = wrap.querySelector('ry-example');
 * await expectEqual(!!el.querySelector('[data-ry-target="preview"]'), true);
 * await expectEqual(!!el.querySelector('[data-ry-target="code"]'), true);
 * ```
 *
 * @tutorial preview-renders-template-content-live
 * ```js
 * const wrap = document.createElement('div');
 * wrap.innerHTML = '<ry-example title="Live"><template><span id="rendered">live</span></template></ry-example>';
 * document.body.appendChild(wrap);
 * await sleep(20);
 * const preview = wrap.querySelector('[data-ry-target="preview"]');
 * const inner = preview.querySelector('#rendered');
 * await expectEqual(!!inner, true);
 * await expectEqual(inner.textContent, 'live');
 * ```
 *
 * @tutorial code-slot-contains-ry-code-with-source
 * ```js
 * const wrap = document.createElement('div');
 * wrap.innerHTML = '<ry-example title="Source"><template><span>shown</span></template></ry-example>';
 * document.body.appendChild(wrap);
 * await sleep(20);
 * const codeBlock = wrap.querySelector('[data-ry-target="code"] ry-code');
 * await expectEqual(!!codeBlock, true);
 * await expectAttr(codeBlock, 'language', 'html');
 * ```
 *
 * @tutorial stacked-attribute-sets-data-stacked-on-host
 * ```js
 * const wrap = document.createElement('div');
 * wrap.innerHTML = '<ry-example stacked><template><span>x</span></template></ry-example>';
 * document.body.appendChild(wrap);
 * await sleep(20);
 * const el = wrap.querySelector('ry-example');
 * await expectEqual(el.hasAttribute('data-stacked'), true);
 * ```
 *
 * @tutorial js-usage-script-renders-toggle-and-collapsed-panel
 * ```js
 * const wrap = document.createElement('div');
 * wrap.innerHTML =
 *   '<ry-example><template><span>x</span></template>' +
 *   '<script slot="js" type="text/plain">console.log(1);</script>' +
 *   '</ry-example>';
 * document.body.appendChild(wrap);
 * await sleep(20);
 * const el = wrap.querySelector('ry-example');
 * await expectEqual(!!el.querySelector('[data-ry-target="usage"]'), true);
 * const toggle = el.querySelector('.ry-example__usage-toggle');
 * await expectEqual(!!toggle, true);
 * await expectAttr(toggle, 'aria-expanded', 'false', 'collapsed by default');
 * const panel = el.querySelector('.ry-example__usage-panel');
 * await expectEqual(panel.hidden, true);
 * ```
 *
 * @tutorial usage-toggle-expands-panel
 * ```js
 * const wrap = document.createElement('div');
 * wrap.innerHTML =
 *   '<ry-example><template><span>x</span></template>' +
 *   '<script slot="js" type="text/plain">console.log(1);</script>' +
 *   '</ry-example>';
 * document.body.appendChild(wrap);
 * await sleep(20);
 * const el = wrap.querySelector('ry-example');
 * const toggle = el.querySelector('.ry-example__usage-toggle');
 * const panel = el.querySelector('.ry-example__usage-panel');
 * toggle.click();
 * await expectAttr(toggle, 'aria-expanded', 'true');
 * await expectEqual(panel.hidden, false);
 * toggle.click();
 * await expectAttr(toggle, 'aria-expanded', 'false', 'second click collapses');
 * await expectEqual(panel.hidden, true);
 * ```
 *
 * @see ry-code
 */

import { RyElement } from '../core/ry-element.js';
import { transform } from '../core/ry-transform.js';

export class RyExample extends RyElement {
  setup(): void {
    const template = this.$<HTMLTemplateElement>('template');
    if (!template) return;

    // Get JS usage script (must capture before clearing innerHTML)
    const jsScript = this.$<HTMLScriptElement>('script[slot="js"]');
    const jsSource = jsScript ? this.#trimIndent(jsScript.textContent ?? '') : null;

    // Get clean source (with normalized indentation)
    const source = this.#trimIndent(template.innerHTML);

    // Transform for live rendering
    const transformed = transform(source);

    // Build layout structure
    const stacked = this.hasAttribute('stacked');
    this.innerHTML = `
      <div data-ry-target="preview" class="ry-example__preview"></div>
      <div data-ry-target="code" class="ry-example__code"></div>
      ${jsSource ? '<div data-ry-target="usage" class="ry-example__usage"></div>' : ''}
    `;

    if (stacked) {
      this.setAttribute('data-stacked', '');
    }

    // Create and inject ry-code with source
    const codeEl = document.createElement('ry-code');
    codeEl.setAttribute('language', 'html');
    codeEl.setAttribute('title', this.getAttribute('title') ?? 'Example');
    codeEl.textContent = source;
    this.$('[data-ry-target="code"]')!.appendChild(codeEl);

    // Create and inject live preview
    const previewEl = this.$<HTMLElement>('[data-ry-target="preview"]')!;
    const previewTemplate = document.createElement('template');
    previewTemplate.innerHTML = transformed;
    previewEl.appendChild(previewTemplate.content);

    // Append responsive-resize handle (Bootstrap Icons grip-vertical, as used by Shoelace docs)
    previewEl.insertAdjacentHTML(
      'beforeend',
      `<div class="ry-example__resizer" data-ry-target="preview-resizer" aria-hidden="true" title="Drag to resize">
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16"><path d="M7 2a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM7 5a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zM7 8a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm-3 3a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm-3 3a1 1 0 1 1-2 0 1 1 0 0 1 2 0zm3 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0z"/></svg>
      </div>`
    );
    this.#wireResizer(previewEl);

    // Add JS usage section if provided
    if (jsSource) {
      this.#buildUsageSection(jsSource);
    }
  }

  #wireResizer(previewEl: HTMLElement): void {
    const resizer = previewEl.querySelector<HTMLElement>('[data-ry-target="preview-resizer"]');
    if (!resizer) return;

    const MIN_WIDTH = 240;

    this.on(resizer, 'pointerdown', (event: Event) => {
      const e = event as PointerEvent;
      e.preventDefault();
      const startX = e.clientX;
      const startWidth = previewEl.getBoundingClientRect().width;
      resizer.setPointerCapture(e.pointerId);
      previewEl.classList.add('ry-example__preview--dragging');

      const move = (ev: PointerEvent) => {
        const width = Math.max(MIN_WIDTH, startWidth + (ev.clientX - startX));
        previewEl.style.width = `${width}px`;
      };
      const stop = (ev: PointerEvent) => {
        previewEl.classList.remove('ry-example__preview--dragging');
        try { resizer.releasePointerCapture(ev.pointerId); } catch {}
        resizer.removeEventListener('pointermove', move);
        resizer.removeEventListener('pointerup', stop);
        resizer.removeEventListener('pointercancel', stop);
      };
      resizer.addEventListener('pointermove', move);
      resizer.addEventListener('pointerup', stop);
      resizer.addEventListener('pointercancel', stop);
    });
  }

  #buildUsageSection(jsSource: string): void {
    const usageContainer = this.$('[data-ry-target="usage"]');
    if (!usageContainer) return;

    // Create toggle button
    const toggle = document.createElement('button');
    toggle.className = 'ry-example__usage-toggle';
    toggle.type = 'button';
    toggle.innerHTML = '<span>JS Usage</span><ry-icon name="chevron-down" size="16"></ry-icon>';
    toggle.setAttribute('aria-expanded', 'false');

    // Create content panel
    const panel = document.createElement('div');
    panel.className = 'ry-example__usage-panel';
    panel.hidden = true;

    // Create code block for JS
    const jsCodeEl = document.createElement('ry-code');
    jsCodeEl.setAttribute('language', 'js');
    jsCodeEl.setAttribute('title', 'JavaScript');
    jsCodeEl.textContent = jsSource;
    panel.appendChild(jsCodeEl);

    usageContainer.appendChild(toggle);
    usageContainer.appendChild(panel);

    // Toggle handler
    this.on(toggle, 'click', () => {
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!expanded));
      panel.hidden = expanded;
    });
  }

  /**
   * Normalize indentation by removing common leading whitespace
   */
  #trimIndent(html: string): string {
    const lines = html.split('\n');

    // Remove empty first/last lines
    while (lines.length && !lines[0]!.trim()) lines.shift();
    while (lines.length && !lines[lines.length - 1]!.trim()) lines.pop();

    if (lines.length === 0) return '';

    // Find minimum indentation (ignoring empty lines)
    const minIndent = Math.min(
      ...lines
        .filter(line => line.trim())
        .map(line => {
          const match = line.match(/^(\s*)/);
          return match?.[1]?.length ?? 0;
        })
    );

    // Remove common indentation
    return lines
      .map(line => line.slice(minIndent))
      .join('\n')
      .trim();
  }
}

customElements.define('ry-example', RyExample);
